import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useState } from "react";
import { LngLatBounds } from "mapbox-gl";
import bbox from "@turf/bbox";
import { useModal } from "@/hooks/useModal";
import { useBreakpointValue } from "@gemini/core";
import { ModalIds } from "@/constants";
import { useLocation } from "@/location/hooks/useLocation";
import { MAP_STYLES } from "@/location/constants";
export const MapContext = createContext(undefined);
const DEFAULT_ZOOM = 14;
const DEFAULT_STYLE = MAP_STYLES.STANDARD;
const DEFAULT_CENTER = [2.333333, 48.866667]; // Paris
const DEFAULT_BEARING = 0;
export const DEFAULT_PITCH = 0;
export const THREE_D_PITCH = 60;
export const THREE_D_ZOOM = 17;
const useViewportState = () => {
    const { geometry } = useLocation() || {};
    if (!geometry) {
        return {
            zoom: DEFAULT_ZOOM,
            center: DEFAULT_CENTER,
            pitch: DEFAULT_PITCH,
            bearing: DEFAULT_BEARING,
            bounds: new LngLatBounds(DEFAULT_CENTER, DEFAULT_CENTER),
        };
    }
    if (geometry.type === "Point") {
        return {
            zoom: DEFAULT_ZOOM,
            center: geometry.coordinates,
            pitch: DEFAULT_PITCH,
            bearing: DEFAULT_BEARING,
            bounds: new LngLatBounds(geometry.coordinates, geometry.coordinates),
        };
    }
    if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
        const polygonGeometry = geometry;
        const bounds = bbox(polygonGeometry);
        const center = [
            (bounds[0] + bounds[2]) / 2,
            (bounds[1] + bounds[3]) / 2,
        ];
        return {
            zoom: DEFAULT_ZOOM,
            center,
            pitch: DEFAULT_PITCH,
            bearing: DEFAULT_BEARING,
            bounds: new LngLatBounds([bounds[0], bounds[1]], // Southwest corner
            [bounds[2], bounds[3]] // Northeast corner
            ),
        };
    }
};
export const MapProvider = ({ children }) => {
    const [map, setMap] = useState(null);
    const [viewport, setViewport] = useState(useViewportState());
    const [style, setStyle] = useState(DEFAULT_STYLE);
    const [layers, setLayers] = useState({ sunlight: false });
    const date = new Date();
    const minutesSinceMidnight = date.getHours() * 60 + date.getMinutes();
    const [time, setTime] = useState(minutesSinceMidnight);
    const [error, setError] = useState(null);
    const [is3DMode, setIs3DMode] = useState(false);
    const { isOpen, open, close, toggle } = useModal(false, ModalIds.MAP);
    const breakpoint = useBreakpointValue({
        base: "base",
        sm: "sm",
        md: "md",
        lg: "lg",
    });
    const isMobile = breakpoint === "base" || breakpoint === "sm";
    const fitBoundsOptions = useMemo(() => ({
        padding: isMobile
            ? { top: 150, bottom: 300, left: 50, right: 50 }
            : { top: 150, bottom: 150, left: 630, right: 150 },
    }), [isMobile]);
    const [initialLights, setInitialLights] = useState([]);
    const value = useMemo(() => {
        return {
            map,
            style,
            viewport,
            error,
            setMap,
            setStyle,
            setViewport,
            setError,
            is3DMode,
            setIs3DMode,
            isOpen,
            open,
            close,
            toggle,
            fitBoundsOptions,
            layers,
            setLayers,
            time,
            setTime,
            initialLights,
            setInitialLights,
        };
    }, [
        map,
        style,
        viewport,
        layers,
        error,
        is3DMode,
        isOpen,
        open,
        close,
        toggle,
        fitBoundsOptions,
        time,
        initialLights,
    ]);
    return _jsx(MapContext.Provider, { value: value, children: children });
};
export const useMapContext = () => {
    const context = useContext(MapContext);
    if (context === undefined) {
        throw new Error("useMapContext must be used within a MapProvider");
    }
    return context;
};
